import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { gettext } from '@eb/i18n-ebspa';

import { Calendar as CalendarSVG } from '@eb/eds-iconography';

import ConnectedEventsBucket from '../containers/ConnectedEventsBucket';
import ConnectedOrganizersBucket from '../containers/ConnectedOrganizersBucket';
import ConnectedOrganizerArticleBucket from '../containers/ConnectedOrganizerArticleBucket';
import {
    retrieveOrgToFollow,
    removeOrgToFollow,
} from '../utils/sessionStorage';
import { getBuckets, buildBuckets } from '../utils/feed';
import { PROFILE_TYPE, ORGANIZER_ARTICLE_TYPE } from '../constants/constants';
import {
    BUCKETS_PROPTYPES,
    FEED_CONFIG_PROPTYPES,
    FEATURE_FLAGS_PROPTYPES,
    EVENT_SEARCH_PROPTYPES,
    FOLLOW_PROPTYPES,
} from '../constants/propTypes';

import LoadingBucket from './LoadingBucket';

import './EventsBucket.scss';
import StateMessage from './StateMessage';

export default class FeedBuilderTabs extends PureComponent {
    static propTypes = {
        getOrganizersUserFollow: PropTypes.func,
        fetchEventsForTab: PropTypes.func,
        followOrganizer: PropTypes.func,
        buckets: BUCKETS_PROPTYPES.isRequired,
        events: BUCKETS_PROPTYPES.isRequired,
        featureFlags: FEATURE_FLAGS_PROPTYPES.isRequired,
        feedConfig: FEED_CONFIG_PROPTYPES.isRequired,
        eventSearch: EVENT_SEARCH_PROPTYPES,
        follow: FOLLOW_PROPTYPES,
        verifyPoorContent: PropTypes.func,
        shouldSuggestReloadingPage: PropTypes.bool,
        tabKey: PropTypes.string,
        isAuthenticated: PropTypes.bool,
        loadingState: PropTypes.shape({
            isFeLoading: PropTypes.bool,
            isSearchLoading: PropTypes.bool,
            isCityBrowseLoading: PropTypes.bool,
            isProfilesLoading: PropTypes.bool,
            isOrganizerArticlesLoading: PropTypes.bool,
        }),
    };

    static defaultProps = {
        loadingState: {},
    };

    state = {
        isInBrowser: false,
    };

    componentDidMount() {
        const {
            buckets,
            events,
            verifyPoorContent,
            followOrganizer,
        } = this.props;

        const organizerToFollow = retrieveOrgToFollow();

        if (organizerToFollow) {
            followOrganizer(organizerToFollow);
            removeOrgToFollow();
        }

        if (verifyPoorContent) {
            verifyPoorContent({
                buckets,
                events,
                actionName: 'on_tab_loading',
            });
        }

        this.setState({ isInBrowser: true });
    }

    // eslint-disable-next-line complexity
    render() {
        const {
            buckets,
            events,
            featureFlags,
            feedConfig,
            loadingState: { isFeLoading, isSearchLoading, isCityBrowseLoading },
            shouldSuggestReloadingPage,
        } = this.props;

        const allFlatBuckets = buildBuckets({
            events,
            buckets,
            feedConfig,
            isCityBrowseLoading,
        });

        const allBuckets = getBuckets({
            buckets: allFlatBuckets,
            featureFlags,
            feedConfig,
        });

        let renderedBuckets = [
            <div className="feed-events-bucket" key="loading-component">
                <div className="feed-events-bucket__content feed-events-bucket__content--loading">
                    <LoadingBucket />
                </div>
            </div>,
        ];

        // Only show this when in browser, not server, to avoid hydration mismatch
        if (!isFeLoading && this.state.isInBrowser) {
            if (allBuckets.length === 0 && shouldSuggestReloadingPage) {
                // The server is temporarily unable to return events
                return (
                    <StateMessage
                        title={gettext('Unable to load events')}
                        description={gettext('Try again or check back later')}
                        iconType={<CalendarSVG />}
                        iconSize="medium"
                        primaryLink="/"
                        primaryText={gettext('Try again')}
                    />
                );
            }

            if (!isSearchLoading) {
                renderedBuckets = allBuckets.reduce(
                    (
                        memo,
                        { key, type, bucketConfig, ...bucketProps },
                        index,
                    ) => {
                        const lastIndex = allBuckets.length - 1;
                        const separator =
                            index < lastIndex ? (
                                <hr
                                    key={`separator-${key}`}
                                    className="feed-events-bucket__separator"
                                />
                            ) : null;

                        if (type === 'event') {
                            return [
                                ...memo,
                                <ConnectedEventsBucket
                                    key={key}
                                    bucketConfig={bucketConfig}
                                    bucketKey={key}
                                    shouldUseWaypoints={index > 0}
                                    // Always show see more for "Organizers you follow"
                                    shouldShowSeeMore={
                                        index === lastIndex ||
                                        key === 'followed_profiles'
                                    }
                                    featureFlags={featureFlags}
                                    {...bucketProps}
                                />,
                                separator,
                            ];
                        }

                        if (type === PROFILE_TYPE) {
                            return [
                                ...memo,
                                <ConnectedOrganizersBucket
                                    key={key}
                                    bucketConfig={bucketConfig}
                                    bucketKey={key}
                                    shouldUseWaypoints={index > 0}
                                    {...bucketProps}
                                />,
                                separator,
                            ];
                        }

                        if (type === ORGANIZER_ARTICLE_TYPE) {
                            return [
                                ...memo,
                                <ConnectedOrganizerArticleBucket
                                    key={key}
                                    bucketConfig={bucketConfig}
                                    bucketKey={key}
                                    shouldUseWaypoints={index > 0}
                                    {...bucketProps}
                                />,
                                separator,
                            ];
                        }

                        return memo;
                    },
                    [],
                );
            }
        }

        return <div className="feed-builder">{renderedBuckets}</div>;
    }
}
